import { useCreateScheduledTask } from '@services/cron/mutations/useCreateScheduledTask'
import { useTaskCatalog } from '@services/cron/queries/useTaskCatalog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { CronEditor, type CronFields } from './CronEditor'

const DEFAULT_CRON: CronFields = {
  minute: '0',
  hour: '0',
  day: '*',
  month: '*',
  day_of_week: '*'
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateCronJobDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation('cron')
  const catalog = useTaskCatalog()
  const create = useCreateScheduledTask()

  const [taskKey, setTaskKey] = useState<string>()
  const [cron, setCron] = useState(DEFAULT_CRON)
  const [description, setDescription] = useState('')

  const options = (catalog.data ?? [])
    .filter((item) => !item.is_scheduled)
    .map((item) => ({ label: item.label, value: item.key }))

  function reset() {
    setTaskKey(undefined)
    setCron(DEFAULT_CRON)
    setDescription('')
  }

  function submit() {
    if (!taskKey) return
    create.mutate(
      { body: { task_key: taskKey, description, ...cron } },
      {
        onSuccess: () => {
          onOpenChange(false)
          reset()
        }
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('create.task')}</Label>
            <Combobox
              options={options}
              value={taskKey}
              onChange={setTaskKey}
              placeholder={t('create.task')}
              emptyText={t('create.noTasks')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('detail.schedule')}</Label>
            <CronEditor value={cron} onChange={setCron} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('create.description')}</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button disabled={!taskKey || create.isPending} onClick={submit}>
            {t('actions.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
